/*
 * The visa / workflow option groups shown by every visa picker (the Formik and
 * standalone `VisaTypeSelector`, the case header's workflow row). Data only, so
 * pickers built on different UI kits share one list.
 */
import {
  FaHandHoldingHeart,
  FaUsers,
  type IconType,
  MdBusiness,
  MdGavel,
  MdOutlineHowToReg
} from "@/components/icons/legacy";

import { MapVisaToFeatureType, VISAVALUE } from "../../types/visa.type";
import { NOIR_NOID_WORKFLOW_OPTION } from "../../types/workflow.type";

export const RFE_VISA_CATEGORY_ID = "rfe-noid-noir";

/** Workflows no case can move to or start on; existing cases keep theirs. */
export const RETIRED_VISAS: string[] = [VISAVALUE.EB2NIW];

export interface VisaOption {
  value: string;
  label: string;
  tag?: string;
}

export interface VisaCategory {
  id: string;
  title: string;
  icon: IconType | undefined;
  visas: VisaOption[];
}

// Helper to create visa option
const createVisaOption = (
  value: VISAVALUE | string,
  label?: string
): VisaOption => ({
  value,
  label: label || value,
  tag:
    typeof value === "string" && value in MapVisaToFeatureType
      ? MapVisaToFeatureType[value as keyof typeof MapVisaToFeatureType]
      : undefined
});

// Sort visas alphabetically by value and build the corresponding options.
const buildSortedVisaOptions = (visas: (VISAVALUE | string)[]): VisaOption[] =>
  [...visas]
    .sort((a, b) => a.localeCompare(b))
    .map((visa) => createVisaOption(visa));

// The legacy and new NIW workflows are distinct VisaType values but must both
// display as "EB-2 NIW" in the picker, differentiated only by a version tag.
const applyNiwVersionTag = (option: VisaOption): VisaOption => {
  if (option.value === VISAVALUE.EB2NIW) {
    return { ...option, label: "EB-2 NIW", tag: "Deprecated version" };
  }
  if (option.value === VISAVALUE.EB2NIW_V2) {
    return { ...option, label: "EB-2 NIW", tag: "New version" };
  }
  return option;
};

/** The NIW version tags, dropped when the picker lists only one version. */
export const withoutLoneVersionTags = (options: VisaOption[]): VisaOption[] => {
  const niw = options.filter(
    (o) => o.value === VISAVALUE.EB2NIW || o.value === VISAVALUE.EB2NIW_V2
  );
  if (niw.length !== 1) return options;
  return options.map((o) => (o === niw[0] ? { ...o, tag: undefined } : o));
};

// The current NIW workflow lists just above the deprecated one it replaces.
const newNiwFirst = (options: VisaOption[]): VisaOption[] => {
  const current = options.find((o) => o.value === VISAVALUE.EB2NIW_V2);
  if (!current) return options;
  return options.flatMap((o) =>
    o.value === VISAVALUE.EB2NIW_V2
      ? []
      : o.value === VISAVALUE.EB2NIW
      ? [current, o]
      : [o]
  );
};

const flag = (name: string, visa: VISAVALUE): VISAVALUE[] =>
  import.meta.env[name] === "true" ? [visa] : [];

// Visa categories configuration (sections sorted A–Z by title for the picker)
const VISA_CATEGORIES_UNSORTED: VisaCategory[] = [
  {
    id: "employment-based",
    title: "BUSINESS IMMIGRATION",
    icon: MdBusiness,
    visas: newNiwFirst(
      buildSortedVisaOptions([
        VISAVALUE.E1,
        VISAVALUE.E2,
        VISAVALUE.E3,
        VISAVALUE.EB1A,
        VISAVALUE.EB1B,
        VISAVALUE.EB1C,
        VISAVALUE.EB2NIW,
        VISAVALUE.EB2NIW_V2,
        ...flag("REACT_APP_ENABLE_EB5", VISAVALUE.EB5),
        ...flag("REACT_APP_ENABLE_I829_WORKFLOW", VISAVALUE.I829),
        VISAVALUE.H1B,
        VISAVALUE.L1A,
        VISAVALUE.L1B,
        VISAVALUE.O1A,
        VISAVALUE.O1B,
        ...flag("REACT_APP_ENABLE_PERM_WORKFLOW", VISAVALUE.PERM),
        VISAVALUE.SCHEDULE_A_G2,
        VISAVALUE.TN,
        ...flag("REACT_APP_ENABLE_STEMOPT_WORKFLOW", VISAVALUE.STEMOPT),
        ...flag(
          "REACT_APP_ENABLE_I140_APPROVED_LC_WORKFLOW",
          VISAVALUE.I140_APPROVED_LC
        ),
        ...flag("REACT_APP_ENABLE_P1A_WORKFLOW", VISAVALUE.P1A),
        ...flag("REACT_APP_ENABLE_P1B_WORKFLOW", VISAVALUE.P1B),
        ...flag("REACT_APP_ENABLE_R1_WORKFLOW", VISAVALUE.R1),
        ...flag("REACT_APP_ENABLE_H2B_WORKFLOW", VISAVALUE.H2B),
        ...flag("REACT_APP_ENABLE_H2A_WORKFLOW", VISAVALUE.H2A),
        ...flag("REACT_APP_ENABLE_H3_WORKFLOW", VISAVALUE.H3),
        ...flag("REACT_APP_ENABLE_B1_WORKFLOW", VISAVALUE.B1),
        ...flag("REACT_APP_ENABLE_O2_WORKFLOW", VISAVALUE.O2),
        ...flag("REACT_APP_ENABLE_P3_WORKFLOW", VISAVALUE.P3)
      ]).map(applyNiwVersionTag)
    )
  },
  {
    id: "family-based",
    title: "FAMILY-BASED",
    icon: FaUsers,
    visas: buildSortedVisaOptions([
      VISAVALUE.I130,
      ...flag("REACT_APP_ENABLE_I751_WORKFLOW", VISAVALUE.I751),
      ...flag("REACT_APP_ENABLE_K1_WORKFLOW", VISAVALUE.K1)
    ])
  },
  {
    id: "adjustment-naturalization",
    title: "ADJUSTMENT & NATURALIZATION",
    icon: MdOutlineHowToReg,
    visas: buildSortedVisaOptions([
      VISAVALUE.AOS,
      VISAVALUE.N400,
      ...flag("REACT_APP_ENABLE_N600_WORKFLOW", VISAVALUE.N600)
    ])
  },
  {
    id: "humanitarian",
    title: "HUMANITARIAN",
    icon: FaHandHoldingHeart,
    visas: buildSortedVisaOptions([
      VISAVALUE.I589,
      VISAVALUE.I601,
      VISAVALUE.I601A,
      VISAVALUE.U,
      VISAVALUE.VAWA,
      ...flag("REACT_APP_ENABLE_T_WORKFLOW", VISAVALUE.T),
      ...flag("REACT_APP_ENABLE_I212_WORKFLOW", VISAVALUE.I212),
      ...flag("REACT_APP_ENABLE_I612_WORKFLOW", VISAVALUE.I612),
      ...flag("REACT_APP_ENABLE_I602_WORKFLOW", VISAVALUE.I602),
      ...flag("REACT_APP_ENABLE_SIJS_WORKFLOW", VISAVALUE.SIJS),
      ...flag("REACT_APP_ENABLE_TPS_WORKFLOW", VISAVALUE.TPS),
      ...flag(
        "REACT_APP_ENABLE_PAROLE_IN_PLACE_WORKFLOW",
        VISAVALUE.PAROLE_IN_PLACE
      ),
      ...flag(
        "REACT_APP_ENABLE_MILITARY_PAROLE_IN_PLACE_WORKFLOW",
        VISAVALUE.MILITARY_PAROLE_IN_PLACE
      )
    ])
  },
  {
    id: "removal-defense",
    title: "REMOVAL DEFENSE",
    icon: MdGavel,
    visas: buildSortedVisaOptions([
      ...flag("REACT_APP_ENABLE_42A_WORKFLOW", VISAVALUE.EOIR_42A),
      ...flag("REACT_APP_ENABLE_EOIR_42B_WORKFLOW", VISAVALUE.EOIR_42B),
      ...flag("REACT_APP_ENABLE_BOND_WORKFLOW", VISAVALUE.BOND),
      ...flag(
        "REACT_APP_ENABLE_VAWA_CANCELLATION_WORKFLOW",
        VISAVALUE.VAWA_CANCELLATION
      ),
      ...flag("REACT_APP_ENABLE_I246_WORKFLOW", VISAVALUE.I246),
      ...flag(
        "REACT_APP_ENABLE_MOTION_TO_REOPEN_RECONSIDER_WORKFLOW",
        VISAVALUE.MOTION_TO_REOPEN_RECONSIDER
      )
    ])
  },
  {
    id: "federal-litigation",
    title: "FEDERAL LITIGATION",
    icon: MdGavel,
    visas: buildSortedVisaOptions([
      ...flag("REACT_APP_ENABLE_HABEAS_WORKFLOW", VISAVALUE.HABEAS),
      ...flag("REACT_APP_ENABLE_MANDAMUS_WORKFLOW", VISAVALUE.MANDAMUS)
    ])
  },
  {
    id: RFE_VISA_CATEGORY_ID,
    title: "RESPOND TO A NOTICE",
    icon: undefined,
    visas: [
      { value: "RFE", label: "RFE Response", tag: "" },
      { value: NOIR_NOID_WORKFLOW_OPTION, label: "NOID/NOIR Response", tag: "" }
    ]
  },
  {
    id: "other",
    title: "OTHER",
    icon: undefined,
    visas: buildSortedVisaOptions([
      ...flag(
        "REACT_APP_ENABLE_CUSTOM_TEMPLATE_WORKFLOW",
        VISAVALUE.CUSTOM_TEMPLATE
      )
    ])
  }
];

export const VISA_CATEGORIES: VisaCategory[] = [
  ...VISA_CATEGORIES_UNSORTED
].sort((a, b) => {
  // Answering a notice is the most time-bound work: it leads the picker.
  if (a.id === RFE_VISA_CATEGORY_ID) return -1;
  if (b.id === RFE_VISA_CATEGORY_ID) return 1;
  if (a.id === "other") return 1;
  if (b.id === "other") return -1;
  return a.title.localeCompare(b.title);
});

/**
 * Visa categories a case's workflow can be changed to: the RFE / NOID / NOIR
 * entries are workflow *types* layered on a base visa, not visas, so they are
 * left out, as are retired workflows. Empty categories (all flags off) are
 * dropped.
 */
export const visaCategoriesForCase = (): VisaCategory[] =>
  VISA_CATEGORIES.filter((c) => c.id !== RFE_VISA_CATEGORY_ID)
    .map((c) => ({
      ...c,
      visas: withoutLoneVersionTags(
        c.visas.filter((v) => !RETIRED_VISAS.includes(v.value))
      )
    }))
    .filter((c) => c.visas.length > 0);

/** Picker label for a stored visa value (handles the NIW version tags). */
export const visaOptionFor = (value: string): VisaOption | undefined =>
  VISA_CATEGORIES.flatMap((c) => c.visas).find((v) => v.value === value);
